import {ChangeDetectionStrategy, Component, inject, ViewChild} from '@angular/core';
import {RouterActions, RouterSelectors, TableDefinition} from '@store/aviary/aviary.adapter';
import {CatalogRecord, FilterChipEntry} from '@store/catalog/catalog.types';
import {CatalogSelectors} from '@store/catalog/catalog.selectors';
import {TableComponent} from '@aviary/components/table/table.component';
import {CURRENT_ROW_OBJECT} from '@aviary/components/table/table.component.types';
import {MatCard, MatCardContent, MatCardTitle} from '@angular/material/card';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {CoverAndDetailsColumnComponent} from '@app/catalog/search/table-column-components/cover-and-details-column.component';
import {AvailabilityColumnComponent} from '@app/catalog/search/table-column-components/availability-column.component';
import {
  CombinedCoverDetailsAvailabilityColumnComponent
} from '@app/catalog/search/table-column-components/combined-cover-details-availability-column.component';
import {ViewportSelectors} from '@aviary/store/viewport/viewport.selectors';
import {NgClass, NgTemplateOutlet} from '@angular/common';
import {CatalogFiltersComponent} from '@app/catalog/search/catalog-filters/catalog-filters.component';
import {TextButtonComponent} from '@aviary/buttons/text-button/text-button.component';
import {MatSidenav, MatSidenavContainer, MatSidenavContent} from '@angular/material/sidenav';
import {filter} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {createSelector, Store} from '@ngrx/store';
import {MatChip, MatChipListbox} from '@angular/material/chips';
import {IconButtonComponent} from '@aviary/buttons/icon-button/icon-button.component';
import {MxSelector} from '@aviary/store/store.types';
import {Params} from '@angular/router';
import {ListFilterData} from '@aviary/store/common/common.types';
import {
  buildSelectedValuesFrom,
  convertFilterQueryParamValueToArray,
  createFilterParamUpdateActionFor,
  createFilterQueryParamNameFrom
} from '@store/catalog/catalog.helpers';
import {snapshot} from '@aviary/store/store.helpers';
import {isPopulatedArray} from '@aviary/store/common/typing.helpers';

@Component({
  selector: 'px-catalog-search',
  templateUrl: './catalog-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CatalogFiltersComponent,
    MatCard,
    MatCardContent,
    MatCardTitle,
    MatSidenav,
    MatSidenavContainer,
    MatSidenavContent,
    NgClass,
    NgTemplateOutlet,
    SelectPipe,
    TableComponent,
    TextButtonComponent,
    IconButtonComponent,
    MatChip,
    MatChipListbox,

  ]
})
export class CatalogSearchComponent {
  @ViewChild(MatSidenav) sidenav: MatSidenav;

  public readonly routerSelectors = RouterSelectors;
  public readonly viewportSelectors = ViewportSelectors;
  public readonly isPopulatedArray = isPopulatedArray;
  private readonly store = inject(Store);

  constructor() {
    this.store.select(this.viewportSelectors.isLgAndUp)
      .pipe(
        filter(isLgAndUp => isLgAndUp),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        if (this.sidenav?.opened) this.sidenav.close();
      });
  }

  public removeFilter(entry: FilterChipEntry): void {
    if (!entry) return;
    const queryParamName = createFilterQueryParamNameFrom(entry.filterName);
    const selectedValues: Set<string> = buildSelectedValuesFrom(snapshot(RouterSelectors.queryParamValue(queryParamName), this.store));
    selectedValues.delete(entry.value);
    this.store.dispatch(createFilterParamUpdateActionFor(queryParamName, [...selectedValues]));
  }

  public removeAllFilters(): void {
    this.store.dispatch(RouterActions.clearAllFilters());
  }

  public readonly tableDefinition: TableDefinition<CatalogRecord, typeof CatalogSelectors.List> = {
    defaultSelectorContainer: CatalogSelectors.List,
    dataSource: 'records',
    responsiveColumnsDefinition: {
      columnNamesSm: ['combinedCoverDetailsAndAvailabilitySm'],
      columnNamesMd: ['coverAndDetails', 'availability'],
      columnNamesLg: ['coverAndDetails', 'availability']
    },
    columns: [
      {
        type: 'component',
        columnName: 'coverAndDetails',
        componentClass: CoverAndDetailsColumnComponent,
        componentProperties: {
          catalogRecord: CURRENT_ROW_OBJECT,
        }
      },
      {
        type: 'component',
        header: 'Availability',
        columnName: 'availability',
        headerCssClasses: 'w-[15.5rem]',
        componentClass: AvailabilityColumnComponent,
        componentProperties: {
          catalogRecord: CURRENT_ROW_OBJECT,
        }
      },
      {
        type: 'component',
        columnName: 'combinedCoverDetailsAndAvailabilitySm',
        componentClass: CombinedCoverDetailsAvailabilityColumnComponent,
        componentProperties: {
          catalogRecord: CURRENT_ROW_OBJECT,
        }
      }
    ],
    hideHeaderRow: true
  };

  public activeFilterOptions: MxSelector<FilterChipEntry[]> = createSelector(
    RouterSelectors.queryParams,
    CatalogSelectors.List.filterData,
    (params: Params, filterData: ListFilterData) => {
      const filterOptions: FilterChipEntry[] = [];
      for (const filterName in filterData) {
        const filter = filterData[filterName];
        const values = params[createFilterQueryParamNameFrom(filterName)];
        for (const value of convertFilterQueryParamValueToArray(values)) {
          const displayName = filter.find((filter) => {
            return filter.filterValue === value;
          })?.displayValue ?? 'Invalid';
          filterOptions.push({filterName, displayName, value});
        }
      }
      return filterOptions
    }
  );
}
